import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../../api/client';
import AdminModal from '../../components/admin/AdminModal';
import { DragHandle, SortableItem, reorder } from './pageEditor/sortable';

export default function AdminStreamers() {
  const [streamers, setStreamers] = useState([]);
  const [form, setForm] = useState({
    twitchUsername: '',
    displayName: '',
    category: '',
    description: '',
    published: true,
    sortOrder: 0,
  });
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [formError, setFormError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const data = await apiGet('/streamers');
      setStreamers(data);
    } catch (err) {
      console.error(err);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => ({
    total: streamers.length,
    live: streamers.filter((s) => s.isLive).length,
    published: streamers.filter((s) => s.published).length,
  }), [streamers]);

  const resetForm = () => {
    setForm({
      twitchUsername: '',
      displayName: '',
      category: '',
      description: '',
      published: true,
      sortOrder: 0,
    });
    setEditingId(null);
  };

  const closeForm = () => {
    setFormOpen(false);
    setFormError('');
    resetForm();
  };

  const startAdd = () => {
    resetForm();
    const top = streamers.length ? Math.max(...streamers.map((s) => s.sortOrder || 0)) + 1 : 0;
    setForm((f) => ({ ...f, sortOrder: top }));
    setFormError('');
    setFormOpen(true);
  };

  const persistOrder = async (next) => {
    const ordered = next.map((s, i) => ({ ...s, sortOrder: next.length - i }));
    setStreamers(ordered);
    try {
      await Promise.all(ordered
        .filter((s, i) => s.sortOrder !== next[i].sortOrder || s.id !== streamers[i]?.id)
        .map((s) => apiPut(`/streamers/${s.id}`, {
          twitchUsername: s.twitchUsername,
          displayName: s.displayName || null,
          category: s.category || null,
          description: s.description || null,
          published: s.published,
          sortOrder: s.sortOrder,
        })));
      setMessage('Order saved.');
    } catch (err) {
      setMessage(err.message);
      load();
    }
  };

  const handleSave = async () => {
    setMessage('');
    setFormError('');
    const payload = {
      twitchUsername: form.twitchUsername.trim(),
      displayName: form.displayName.trim() || null,
      category: form.category.trim() || null,
      description: form.description.trim() || null,
      published: form.published,
      sortOrder: Number(form.sortOrder) || 0,
    };
    try {
      if (editingId) {
        await apiPut(`/streamers/${editingId}`, payload);
        setMessage('Streamer updated.');
      } else {
        await apiPost('/streamers', payload);
        setMessage('Streamer added.');
      }
      resetForm();
      setFormOpen(false);
      load();
    } catch (err) {
      setFormError(err.message);
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({
      twitchUsername: s.twitchUsername,
      displayName: s.displayName || '',
      category: s.category || '',
      description: s.description || '',
      published: s.published,
      sortOrder: s.sortOrder || 0,
    });
    setFormOpen(true);
    setFormError('');
    setMessage('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this streamer from the featured list?')) return;
    await apiDelete(`/streamers/${id}`);
    if (editingId === id) {
      resetForm();
      setFormOpen(false);
    }
    load();
  };

  return (
    <div className="admin-panel-page">
      <div className="admin-panel-toolbar">
        <div className="admin-panel-toolbar-row">
          <div className="admin-stat-chips">
            <div className="admin-stat-chip">
              <span className="admin-stat-value">{stats.total}</span>
              <span className="admin-stat-label">Featured</span>
            </div>
            <div className="admin-stat-chip admin-stat-chip--green">
              <span className="admin-stat-value">{stats.live}</span>
              <span className="admin-stat-label">Live now</span>
            </div>
            <div className="admin-stat-chip">
              <span className="admin-stat-value">{stats.published}</span>
              <span className="admin-stat-label">Published</span>
            </div>
          </div>
          <div className="admin-panel-toolbar-actions">
            <button type="button" className="btn btn-secondary" onClick={load} disabled={refreshing}>
              <i className="fas fa-sync-alt" aria-hidden="true" />
              {refreshing ? 'Refreshing…' : 'Refresh live'}
            </button>
            <button type="button" className="btn btn-primary" onClick={startAdd}>
              <i className="fas fa-plus" aria-hidden="true" /> Add streamer
            </button>
          </div>
        </div>
      </div>

      <AdminModal
        open={formOpen}
        as="form"
        onSubmit={handleSave}
        onClose={closeForm}
        size="md"
        title={editingId ? `Edit ${form.displayName || form.twitchUsername}` : 'Add featured streamer'}
        subtitle="Live status, avatar and viewer counts come from Twitch automatically."
        footer={(
          <>
            {formError && <span className="admin-modal__error">{formError}</span>}
            <button type="button" className="btn btn-secondary" onClick={closeForm}>Cancel</button>
            <button type="submit" className="btn btn-primary">{editingId ? 'Save streamer' : 'Add streamer'}</button>
          </>
        )}
      >
        <div className="admin-modal-fields">
          <div className="admin-field-row">
            <label className="admin-field">
              <span>Twitch username</span>
              <input value={form.twitchUsername} onChange={(e) => setForm({ ...form, twitchUsername: e.target.value })} placeholder="zemaxi" required autoFocus />
            </label>
            <label className="admin-field">
              <span>Display name override</span>
              <input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} placeholder="Uses Twitch name if empty" />
            </label>
          </div>
          <label className="admin-field">
            <span>Category label</span>
            <input list="streamer-categories" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Trophy Hunter, Variety Streamer…" />
          </label>
          <label className="admin-field">
            <span>Custom description</span>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Falls back to the Twitch bio when empty" />
          </label>
          <label className="admin-switch">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
            <span>Published on site</span>
          </label>
        </div>
        <datalist id="streamer-categories">
          {[...new Set(streamers.map((s) => s.category).filter(Boolean))].map((c) => <option key={c} value={c} />)}
        </datalist>
      </AdminModal>

      {message && <p className="admin-message">{message}</p>}

      <div className="admin-list-wrap">
        <ul className="admin-list">
          {streamers.map((s, index) => (
            <SortableItem
              as="li"
              key={s.id}
              listId="streamer-order"
              index={index}
              onMove={(from, to) => persistOrder(reorder(streamers, from, to))}
              className={`admin-list-item ${s.isLive ? 'is-live-row' : ''}`}
            >
              <DragHandle listId="streamer-order" index={index} />
              {s.profilePic ? (
                <img src={s.profilePic} alt="" className="admin-thumb is-round" />
              ) : (
                <span className="admin-thumb is-empty is-round"><i className="fab fa-twitch" aria-hidden="true" /></span>
              )}
              <div className="admin-list-main">
                <div className="admin-list-title-row">
                  <span className="admin-list-title">{s.displayName || s.twitchUsername}</span>
                  <span className="admin-list-sub">@{s.twitchUsername}</span>
                </div>
                <div className="admin-list-pills">
                  {s.isLive && (
                    <span className="admin-pill is-green">
                      LIVE · {s.streamInfo?.viewerCount?.toLocaleString()} viewers
                    </span>
                  )}
                  {s.category && <span className="admin-pill is-purple">{s.category}</span>}
                  <span className={`admin-pill ${s.published ? 'is-green' : 'is-muted'}`}>
                    {s.published ? 'Published' : 'Draft'}
                  </span>
                  {s.isLive && s.streamInfo?.gameName && (
                    <span className="admin-pill is-muted">{s.streamInfo.gameName}</span>
                  )}
                </div>
              </div>
              <div className="admin-list-actions">
                <a
                  href={`https://twitch.tv/${s.twitchUsername}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="admin-icon-btn"
                  title="View on Twitch"
                >
                  <i className="fab fa-twitch" aria-hidden="true" />
                </a>
                <button type="button" className="admin-icon-btn" onClick={() => startEdit(s)} title="Edit">
                  <i className="fas fa-pen" aria-hidden="true" />
                </button>
                <button type="button" className="admin-icon-btn danger" onClick={() => handleDelete(s.id)} title="Remove">
                  <i className="fas fa-trash" aria-hidden="true" />
                </button>
              </div>
            </SortableItem>
          ))}
        </ul>
      </div>
    </div>
  );
}
